import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { api, formatApiErrorDetail } from "@/lib/apiClient";
import { toast } from "sonner";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Loader2, Plug, Save } from "lucide-react";

export default function Settings() {
  const { t } = useTranslation();
  const [s, setS] = useState(null);
  const [form, setForm] = useState({ client_id: "", client_secret: "", refresh_token: "", organization_id: "" });
  const [busy, setBusy] = useState(false);

  const load = () => api.get("/settings/zoho").then((r) => { setS(r.data); setForm((f) => ({ ...f, client_id: r.data.client_id || "", organization_id: r.data.organization_id || "" })); });
  useEffect(() => { load(); }, []);

  const save = async () => {
    setBusy(true);
    try {
      const { data } = await api.put("/settings/zoho", form);
      toast.success(t("settings.savedToast", { mode: data.mode }));
      load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  if (!s)
    return <div className="flex h-64 items-center justify-center"><Loader2 className="h-6 w-6 animate-spin text-blue-600" /></div>;

  return (
    <div className="max-w-2xl space-y-6" data-testid="settings-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-heading text-3xl font-black text-slate-900">{t("settings.title")}</h1>
          <p className="mt-1 text-slate-500">{t("settings.subtitle")}</p>
        </div>
        <Badge data-testid="zoho-mode" className={`border-0 ${s.connected ? "bg-emerald-500 text-white" : "bg-amber-500 text-slate-950"}`}>{s.mode}</Badge>
      </div>

      <Card className="border-slate-200 bg-white p-5 shadow-sm">
        <div className="mb-4 flex items-center gap-2 text-slate-700"><Plug className="h-5 w-5 text-blue-600" /><span className="overline">{t("settings.oauthCredentials")}</span></div>
        {!s.connected && (
          <div className="mb-4 rounded-md border border-amber-300 bg-amber-50 p-3 text-sm text-amber-700">
            {t("settings.mockWarning")}
          </div>
        )}
        <div className="space-y-3">
          <div><Label className="overline text-slate-500">{t("settings.organizationId")}</Label><Input data-testid="zoho-org" value={form.organization_id} onChange={(e) => setForm({ ...form, organization_id: e.target.value })} className="mt-1.5 border-slate-300 bg-white" /></div>
          <div><Label className="overline text-slate-500">{t("settings.clientId")}</Label><Input data-testid="zoho-client-id" value={form.client_id} onChange={(e) => setForm({ ...form, client_id: e.target.value })} className="mt-1.5 border-slate-300 bg-white" /></div>
          <div><Label className="overline text-slate-500">{t("settings.clientSecret")}</Label><Input data-testid="zoho-secret" type="password" placeholder={s.has_secret ? t("settings.savedPlaceholder") : ""} value={form.client_secret} onChange={(e) => setForm({ ...form, client_secret: e.target.value })} className="mt-1.5 border-slate-300 bg-white" /></div>
          <div><Label className="overline text-slate-500">{t("settings.refreshToken")}</Label><Input data-testid="zoho-refresh" type="password" placeholder={s.has_refresh ? t("settings.savedPlaceholder") : ""} value={form.refresh_token} onChange={(e) => setForm({ ...form, refresh_token: e.target.value })} className="mt-1.5 border-slate-300 bg-white" /></div>
        </div>
        <Button data-testid="zoho-save" onClick={save} disabled={busy} className="mt-4 gap-2 bg-blue-600 hover:bg-blue-500"><Save className="h-4 w-4" /> {t("settings.saveCredentials")}</Button>
      </Card>
    </div>
  );
}
